import { CircularProgress, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import { FormikProps } from 'formik';
import { Field } from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import { RULE_EVALUATION_LOADING_STATUS, RULE_EVALUATION_PENDING_STATUS } from './RuleEvaluationListener';

const WIDTH_LABEL = '40%';

interface RuleEvaluationTextInputProps {
  field: Field;
  formik: FormikProps<any>;
}

export function getRuleEvaluationInputDisplayValue(value: any) {
  if (value && typeof value === 'object' && 'label' in value) {
    return value.label ?? '';
  }
  return value ?? '';
}

export function RuleEvaluationTextInput({ field, formik }: RuleEvaluationTextInputProps) {
  const isLoading = Boolean(formik.status?.[RULE_EVALUATION_LOADING_STATUS]);
  const isPendingKTree = Boolean(formik.status?.[RULE_EVALUATION_PENDING_STATUS]?.[field.name]);
  const fieldError = formik.touched[field.name] && formik.errors[field.name];
  const helperText = fieldError || (isPendingKTree ? 'Waiting for the KTree to update.' : undefined);

  return (
    <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1} marginTop={2.5}>
      <Stack width={WIDTH_LABEL}>
        <Stack direction='row' alignItems='center' gap={0.3}>
          <Typography variant='input-label' fontWeight={700}>
            {field.label}
          </Typography>
          {field.description && (
            <Tooltip title={field.description}>
              <HelpOutlinedIcon sx={{ fontSize: '14px' }} />
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <TextField
        name={field.name}
        disabled
        value={isLoading ? 'Loading...' : getRuleEvaluationInputDisplayValue(formik.values[field.name])}
        error={Boolean(fieldError)}
        helperText={helperText}
        InputProps={{
          endAdornment: isLoading ? (
            <InputAdornment position='end'>
              <CircularProgress size={16} />
            </InputAdornment>
          ) : undefined,
        }}
        inputProps={{ 'aria-busy': isLoading }}
        sx={{
          width: '65%',
          '& .Mui-disabled': {
            cursor: 'not-allowed !important',
          },
        }}
        hiddenLabel
        variant='outlined'
        size='small'
        placeholder={isPendingKTree ? 'Pending KTree update' : field.placeholder || 'Evaluated by system'}
      />
    </Stack>
  );
}
